import { useState, useEffect, useCallback, useRef } from "react";
import { t } from "@/lib/i18n";
import { toast } from "sonner";
import { Tag, Trash, PencilSimple, Star, Play } from "@phosphor-icons/react";
import api, { errDetail } from "@/lib/api";
import { MailboxSwitcher, useMailboxApi } from "@/components/MailboxSwitcher";

const COLORS = ["#f97316", "#3b82f6", "#22c55e", "#a855f7", "#ef4444", "#eab308", "#06b6d4", "#64748b"];

export const CategoriesCard = () => {
  const { mailbox, setMailbox, mbApi } = useMailboxApi();
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", color: COLORS[0], description: "", is_vip: false });
  const [showForm, setShowForm] = useState(false);
  const [runLimit, setRunLimit] = useState(200);
  const [runScope, setRunScope] = useState("uncategorized");
  const [job, setJob] = useState(null);
  const [autoTag, setAutoTag] = useState(true);
  const pollRef = useRef(null);
  const bumpTokens = () => import("@/components/TokenWidget").then((m) => m.refreshTokenWidget()).catch(() => {});

  const load = useCallback(() => mbApi.get("/categories").then((r) => setCats(r.data)).catch(() => {}), [mbApi]);
  useEffect(() => {
    load();
    mbApi.get("/me/auto-categorize").then((r) => setAutoTag(!!r.data.enabled)).catch(() => {});
    mbApi.get("/categories/run/status").then((r) => { if (r.data.status === "running") { setJob(r.data); startPoll(); } }).catch(() => {});
    return () => clearInterval(pollRef.current);
  }, [load]); // eslint-disable-line

  const toggleAutoTag = async () => {
    const next = !autoTag;
    setAutoTag(next);
    try {
      await mbApi.put("/me/auto-categorize", { enabled: next });
      toast.success(next ? t("cats.autotag_on", "Auto-categorize ON — new emails will be tagged on arrival") : t("cats.autotag_off", "Auto-categorize OFF"));
    } catch (e) { setAutoTag(!next); toast.error(errDetail(e)); }
  };

  const startPoll = () => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await mbApi.get("/categories/run/status");
        setJob(data);
        bumpTokens();
        if (data.status !== "running") { clearInterval(pollRef.current); if (data.status === "done") toast.success(t("cats.run_done", "Categorization finished — {n} emails tagged", { n: data.tagged })); }
      } catch { clearInterval(pollRef.current); }
    }, 2000);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error(t("cats.name_req", "Give the category a name")); return; }
    try {
      if (editing) await mbApi.put(`/categories/${editing}`, form);
      else await mbApi.post("/categories", form);
      toast.success(editing ? t("cats.updated", "Category updated") : t("cats.created", "Category created"));
      setShowForm(false); setEditing(null); setForm({ name: "", color: COLORS[0], description: "", is_vip: false });
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const remove = async (c) => {
    try { await mbApi.delete(`/categories/${c.id}`); toast.success(t("cats.deleted", "Category deleted")); load(); } catch (e) { toast.error(errDetail(e)); }
  };

  const toggleVip = async (c) => {
    try { await mbApi.put(`/categories/${c.id}`, { ...c, is_vip: !c.is_vip }); load(); } catch (e) { toast.error(errDetail(e)); }
  };

  const run = async () => {
    try {
      const { data } = await mbApi.post("/categories/run", { limit: Number(runLimit) || 200, scope: runScope });
      setJob(data);
      if (data.status === "running") startPoll();
      else if (data.status === "done" && data.total === 0) toast.info(t("cats.nothing", "Nothing to categorize"));
    } catch (e) { toast.error(errDetail(e)); }
  };

  const estTokens = Math.round(((Number(runLimit) || 200) * 210) / 1000);

  return (
    <div data-testid="categories-card" className="bg-white border border-gray-200 rounded-lg p-5 mb-6">
      <MailboxSwitcher mailbox={mailbox} onChange={setMailbox} />
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-heading font-bold text-gray-900 flex items-center gap-2"><Tag size={17} className="text-blue-600" /> {t("cats.title", "Email Categories")}</h3>
        <button data-testid="category-new-btn" onClick={() => { setShowForm(!showForm); setEditing(null); setForm({ name: "", color: COLORS[0], description: "", is_vip: false }); }}
          className="text-sm font-semibold text-blue-600 hover:text-blue-800">{showForm ? t("common.close", "Close") : `+ ${t("cats.new", "New category")}`}</button>
      </div>
      <p className="text-xs text-gray-500 mb-4">{t("cats.sub", "Create your own labels with meanings — the AI tags your emails against them. You can always change a tag by hand.")}</p>

      {showForm && (
        <div data-testid="category-form" className="border border-blue-200 bg-blue-50/40 rounded-md p-3 mb-4 space-y-2">
          <input data-testid="category-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder={t("cats.name_ph", "Category name — e.g. Invoices")} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <textarea data-testid="category-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2}
            placeholder={t("cats.desc_ph", "What does it mean? e.g. Invoices, receipts and payment requests from suppliers")}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
          <div className="flex items-center gap-2">
            {COLORS.map((c) => (
              <button key={c} data-testid={`category-color-${c.slice(1)}`} onClick={() => setForm({ ...form, color: c })}
                className={`w-6 h-6 rounded-full transition-transform ${form.color === c ? "ring-2 ring-offset-2 ring-gray-800 scale-110" : "hover:scale-110"}`}
                style={{ backgroundColor: c }} />
            ))}
            <label className="ml-auto flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
              <input data-testid="category-vip-checkbox" type="checkbox" checked={form.is_vip} onChange={(e) => setForm({ ...form, is_vip: e.target.checked })} />
              ★ {t("cats.vip", "VIP category")}
            </label>
          </div>
          <button data-testid="category-save-btn" onClick={save}
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md px-4 py-1.5 transition-colors">
            {editing ? t("cats.save", "Save changes") : t("cats.create", "Create category")}
          </button>
        </div>
      )}

      <div className="space-y-1.5 mb-4" data-testid="categories-list">
        {cats.map((c) => (
          <div key={c.id} data-testid={`category-row-${c.id}`} className="flex items-center gap-2.5 border border-gray-100 rounded-md px-3 py-2">
            <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: c.color }} />
            <span className="text-sm font-semibold text-gray-900">{c.name}</span>
            <span className="text-xs text-gray-400 truncate flex-1">{c.description}</span>
            <button data-testid={`category-vip-toggle-${c.id}`} onClick={() => toggleVip(c)} title={t("cats.vip_toggle", "Toggle VIP")}
              className={c.is_vip ? "text-amber-400" : "text-gray-300 hover:text-gray-500"}>
              <Star size={15} weight={c.is_vip ? "fill" : "regular"} />
            </button>
            <button data-testid={`category-edit-${c.id}`} onClick={() => { setEditing(c.id); setForm({ name: c.name, color: c.color, description: c.description, is_vip: !!c.is_vip }); setShowForm(true); }}
              className="text-gray-400 hover:text-gray-800 p-0.5"><PencilSimple size={14} /></button>
            <button data-testid={`category-delete-${c.id}`} onClick={() => remove(c)} className="text-gray-400 hover:text-red-600 p-0.5"><Trash size={14} /></button>
          </div>
        ))}
        {cats.length === 0 && <p data-testid="no-categories-message" className="text-xs text-gray-400">{t("cats.none", "No categories yet — create your first one.")}</p>}
      </div>

      <div className="border-t border-gray-100 pt-3">
        <label data-testid="autotag-toggle-label" className="flex items-center gap-2 mb-3 text-sm text-gray-700 cursor-pointer">
          <input data-testid="autotag-toggle" type="checkbox" checked={autoTag} onChange={toggleAutoTag} />
          ⚡ {t("cats.autotag", "Auto-categorize new emails on arrival")} <span className="text-xs text-gray-400">({t("cats.uses_tokens", "uses tokens")})</span>
        </label>
        <div className="flex items-center gap-2 flex-wrap text-sm">
          <span className="text-gray-600 font-medium">{t("cats.run_on", "Run sequence on:")}</span>
          <select data-testid="run-scope-select" value={runScope} onChange={(e) => setRunScope(e.target.value)}
            className="border border-gray-300 rounded-md px-2 py-1 text-sm">
            <option value="uncategorized">{t("cats.scope_uncat", "Uncategorized only")}</option>
            <option value="all">{t("cats.scope_all", "All emails (re-tag)")}</option>
          </select>
          <span className="text-gray-500">{t("cats.last", "last")}</span>
          <input data-testid="run-limit-input" type="number" min="1" max="1000" value={runLimit} onChange={(e) => setRunLimit(e.target.value)}
            className="w-20 border border-gray-300 rounded-md px-2 py-1 text-sm" />
          <span className="text-xs text-gray-400">≈ {estTokens}k {t("cats.tokens", "tokens")}</span>
          <button data-testid="run-sequence-btn" onClick={run} disabled={job?.status === "running" || cats.length === 0}
            className="ml-auto flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md px-4 py-1.5 transition-colors disabled:opacity-50">
            <Play size={13} weight="fill" /> {t("cats.run", "Run sequence")}
          </button>
        </div>
        {job && job.status !== "idle" && (
          <div data-testid="run-progress" className="mt-3">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>{job.status === "running" ? t("cats.running", "Categorizing…") : job.status === "done" ? t("cats.done", "Done") : job.status === "paused_tokens" ? t("cats.paused", "Paused — token pool empty") : job.status}</span>
              <span>{job.done || 0}/{job.total || 0} · {job.tagged || 0} {t("cats.tagged", "tagged")}</span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-blue-600 rounded-full transition-all" style={{ width: `${job.total ? Math.round(((job.done || 0) / job.total) * 100) : 0}%` }} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
